import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { SignalOperationsHeader } from "@/components/user/SignalOperationsHeader";
import { SignalQueueSummary } from "@/components/user/SignalQueueSummary";
import { UserPipelineStatusBar } from "@/components/user/UserPipelineStatusBar";
import { Button } from "@/components/ui/button";
import { apiClient, classifyApiError } from "@/lib/api";

// 2026-07-08 P0 SIGNAL-SORT-TOGGLE-CORRECT-PAGE:
// LegacyUserSignalsPage (eski `/signals-legacy` route) tamamen kaldırıldı.
// Sort mode toggle daha önce yanlışlıkla bu ölü sayfaya eklenmişti (commit 9afd278f).
// Toggle + filter + sort_mode API param artık UserSignalsPage içinde (aşağıda).


const formatSignalTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("tr-TR", {
    hour12: false,
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const UserSignalsPage = () => {
  const navigate = useNavigate();
  const [signals, setSignals] = useState([]);
  const [lifecycle, setLifecycle] = useState(null);
  const [explainSelf, setExplainSelf] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  // 2026-07-08 P0 SIGNAL-SORT-TOGGLE-CORRECT-PAGE:
  // Sort mode toggle daha önce (commit 9afd278f) yanlışlıkla `LegacyUserSignalsPage`
  // (ölü `/signals-legacy` route) içine yazılmıştı. Bu commit'te GERÇEK
  // `/signals` sayfasına taşındı. LegacyUserSignalsPage tümüyle silindi.
  const [signalsSortMode, setSignalsSortMode] = useState(() => {
    try {
      if (typeof localStorage === "undefined") return "priority";
      const stored = String(localStorage.getItem("signals-sort-mode") || "priority").toLowerCase();
      return stored === "recent" ? "recent" : "priority";
    } catch (_e) { return "priority"; }
  });
  const persistSortMode = useCallback((mode) => {
    const normalized = mode === "recent" ? "recent" : "priority";
    setSignalsSortMode(normalized);
    try {
      if (typeof localStorage !== "undefined") localStorage.setItem("signals-sort-mode", normalized);
    } catch (_e) { /* ignore */ }
  }, []);

  const loadSignalData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [signalsRes, lifecycleRes, explainRes] = await Promise.all([
        apiClient.get("/user/signals", { params: { limit: 200, sort_mode: signalsSortMode }, timeout: 15000 }),
        apiClient.get("/user/signals/lifecycle-summary", { timeout: 15000 }),
        apiClient.get("/user/control/explain-self", { params: { market_type: "auto" }, timeout: 15000 }),
      ]);
      setSignals(Array.isArray(signalsRes?.data) ? signalsRes.data : []);
      setLifecycle(lifecycleRes?.data || {});
      setExplainSelf(explainRes?.data || {});
    } catch (error) {
      toast.error(classifyApiError(error, "Signal operasyon verisi yüklenemedi"));
    } finally {
      setIsLoading(false);
    }
  }, [signalsSortMode]);

  useEffect(() => {
    loadSignalData();
  }, [loadSignalData]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const timer = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      loadSignalData();
    }, 30000);
    return () => clearInterval(timer);
  }, [autoRefresh, loadSignalData]);

  const stageCounts = useMemo(() => ({
    SIGNAL_CREATED: { status: Number(lifecycle?.detected_count || 0) > 0 ? "completed" : "waiting", count: Number(lifecycle?.detected_count || 0) },
    REVIEW: { status: Number(lifecycle?.ready_count || 0) > 0 ? "running" : "waiting", count: Number(lifecycle?.ready_count || 0) },
    INTENT: { status: Number(lifecycle?.intent_count || 0) > 0 ? "completed" : "waiting", count: Number(lifecycle?.intent_count || 0) },
    SUBMITTED: { status: Number(lifecycle?.submitted_count || 0) > 0 ? "completed" : "waiting", count: Number(lifecycle?.submitted_count || 0) },
    FILLED: { status: Number(lifecycle?.filled_count || 0) > 0 ? "completed" : "waiting", count: Number(lifecycle?.filled_count || 0) },
  }), [lifecycle]);

  const latestSignalAt = useMemo(
    () => formatSignalTime(signals[0]?.created_at),
    [signals],
  );

  const latestTradeAt = useMemo(
    () => formatSignalTime((lifecycle?.latest_trade_at || null)),
    [lifecycle?.latest_trade_at],
  );

  return (
    <section className="mx-auto w-full max-w-7xl space-y-6 px-4 py-6 md:px-8" data-testid="user-signals-refactor-page">
      <SignalOperationsHeader
        signalMode={String(lifecycle?.signal_mode || "AUTO")}
        botRuntime={String(explainSelf?.bot_state?.bot_status || "unknown")}
        executionPath="signal->review->intent->submitted->filled"
        currentBlocker={String((lifecycle?.current_blockers || [])[0] || "-")}
        lastSignalAt={latestSignalAt}
        lastTradeAt={latestTradeAt}
        onGoScanner={() => navigate("/user/scanner")}
      />

      <SignalQueueSummary counts={lifecycle || {}} />

      <UserPipelineStatusBar
        mode="signal"
        stages={stageCounts}
        summary={lifecycle || {}}
      />

      <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="user-signals-blocker-actions-card">
        <div className="flex flex-wrap items-center justify-between gap-3" data-testid="user-signals-blocker-actions-header">
          <h3 className="text-base font-semibold text-slate-900" data-testid="user-signals-blocker-actions-title">Akış Kontrolleri</h3>
          <label className="flex items-center gap-2 text-sm text-slate-700" data-testid="user-signals-auto-refresh-toggle-wrap">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(event) => setAutoRefresh(Boolean(event.target.checked))}
              data-testid="user-signals-auto-refresh-toggle"
            />
            Auto Refresh
          </label>
        </div>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="user-signals-blocker-actions-buttons">
          <Button type="button" variant="outline" disabled={isActionLoading} onClick={loadSignalData} data-testid="user-signals-refresh-button">Refresh</Button>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="user-signals-actionable-table-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-base font-semibold text-slate-900" data-testid="user-signals-actionable-table-title">Actionable Signals</h3>
          <div role="radiogroup" aria-label="Sinyal sıralama modu" className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white p-1 text-xs" data-testid="user-signals-sort-mode-toggle">
            <button
              type="button"
              role="radio"
              aria-checked={signalsSortMode === "priority"}
              data-testid="user-signals-sort-mode-priority"
              onClick={() => persistSortMode("priority")}
              className={`rounded px-2 py-1 transition ${signalsSortMode === "priority" ? "bg-slate-900 text-white font-semibold" : "text-slate-700 hover:bg-slate-100"}`}
            >En İyi Fırsat</button>
            <button
              type="button"
              role="radio"
              aria-checked={signalsSortMode === "recent"}
              data-testid="user-signals-sort-mode-recent"
              onClick={() => persistSortMode("recent")}
              className={`rounded px-2 py-1 transition ${signalsSortMode === "recent" ? "bg-slate-900 text-white font-semibold" : "text-slate-700 hover:bg-slate-100"}`}
            >En Yeni</button>
          </div>
        </div>
        {isLoading ? (
          <div className="mt-3" data-testid="user-signals-loading"><LoadingSkeleton className="h-20 w-full" /></div>
        ) : (
          <div className="mt-3 overflow-x-auto" data-testid="user-signals-actionable-table-wrap">
            <table className="min-w-full text-sm" data-testid="user-signals-actionable-table">
              <thead data-testid="user-signals-actionable-table-head">
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500" data-testid="user-signals-actionable-table-head-row">
                  <th className="px-2 py-2" data-testid="user-signals-head-symbol">Symbol</th>
                  <th className="px-2 py-2" data-testid="user-signals-head-decision-box">Karar</th>
                  <th className="px-2 py-2" data-testid="user-signals-head-strategy">Strategy</th>
                  <th className="px-2 py-2" data-testid="user-signals-head-status">Status</th>
                  <th className="px-2 py-2" data-testid="user-signals-head-tradeable">Tradeable</th>
                  <th className="px-2 py-2" data-testid="user-signals-head-reason-code">Akış Notu</th>
                  <th className="px-2 py-2" data-testid="user-signals-head-intent">Created Intent ID</th>
                  <th className="px-2 py-2" data-testid="user-signals-head-execution-status">Execution Status</th>
                  <th className="px-2 py-2" data-testid="user-signals-head-created-at">Created At</th>
                </tr>
              </thead>
              <tbody data-testid="user-signals-actionable-table-body">
                {(() => {
                  // 2026-07-08 P0: "Actionable Signals" tablosu SADECE actionable statülerini gösterir.
                  const ACTIONABLE_STATUSES = new Set(["ready", "approved", "queued", "submitted", "filled"]);
                  const actionableSignals = signals.filter((s) => ACTIONABLE_STATUSES.has(String(s?.status || "").toLowerCase()));
                  if (actionableSignals.length === 0) {
                    return (
                      <tr data-testid="user-signals-actionable-empty-row">
                        <td colSpan={12} className="px-2 py-3 text-xs text-slate-500" data-testid="user-signals-actionable-empty-text">Actionable signal yok.</td>
                      </tr>
                    );
                  }
                  return actionableSignals.map((signal) => (
                    <tr key={signal.id} className="border-b border-slate-100" data-testid={`user-signals-actionable-row-${signal.id}`}>
                      <td className="px-2 py-2 font-mono" data-testid={`user-signals-actionable-symbol-${signal.id}`}>{signal.symbol}</td>
                      <td className="px-2 py-2" data-testid={`user-signals-actionable-decision-box-${signal.id}`}>{String(signal.decision_box_code || "BC03").toUpperCase()}</td>
                      <td className="px-2 py-2" data-testid={`user-signals-actionable-strategy-${signal.id}`}>{signal.strategy_code || "-"}</td>
                      <td className="px-2 py-2" data-testid={`user-signals-actionable-status-${signal.id}`}>{signal.status}</td>
                      <td className="px-2 py-2" data-testid={`user-signals-actionable-tradeable-${signal.id}`}>{String(Boolean(signal.tradeable)).toUpperCase()}</td>
                      <td className="px-2 py-2" data-testid={`user-signals-actionable-reason-${signal.id}`}>{signal.blocked_reason_message || signal.blocked_solution_hint || signal.reason_code || signal.blocked_reason_code || "-"}</td>
                      <td className="px-2 py-2" data-testid={`user-signals-actionable-intent-${signal.id}`}>{signal.created_order_intent_id || "-"}</td>
                      <td className="px-2 py-2" data-testid={`user-signals-actionable-execution-status-${signal.id}`}>{signal.execution_intent_status || "-"}</td>
                      <td className="px-2 py-2" data-testid={`user-signals-actionable-created-at-${signal.id}`}>{formatSignalTime(signal.created_at)}</td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="user-signals-next-step-card">
        <h3 className="text-base font-semibold text-slate-900" data-testid="user-signals-next-step-title">Sonraki Adım</h3>
        <p className="mt-2 text-sm text-slate-600" data-testid="user-signals-next-step-description">
          Signal akışı burada görünür. Daha fazla sinyal için scanner kapsamını genişletebilir veya bot ayarlarını güncelleyebilirsiniz.
        </p>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="user-signals-next-step-actions">
          <Button type="button" variant="outline" onClick={() => navigate("/user/scanner")} data-testid="user-signals-next-step-scanner-button">Scanner&apos;a Git</Button>
          <Button type="button" variant="outline" onClick={() => navigate("/user/trades")} data-testid="user-signals-next-step-trades-button">Trades&apos;e Git</Button>
        </div>
      </section>
    </section>
  );
};